import { Box, Container, Stack, Typography, useTheme } from "@mui/material";
import { CostumeIcon, DecorationIcon, MusicIcon, RecipesIcon } from "./icons.js";
import { CostumeSuggestions } from "./costume-suggestions.js";
import { darken } from "color2k";
import { ProductContext } from "../provider.js";
import { useMediaQuery } from "../../utilities/index.js";
import { useTranslation } from "next-i18next";
import Grid from "@mui/material/Unstable_Grid2";
import React from "react";
export const HorizontalLayout = () => {
    const { t } = useTranslation("c-product");
    const theme = useTheme();
    const isTouchDevice = useMediaQuery("(hover: none)");
    const [activeSection, setActiveSection] = React.useState("recipes");
    const { additionalMaterials, colors, title } = React.useContext(ProductContext);
    const { decoration, music, recipes } = additionalMaterials;
    const backgroundColor = colors.tertiary ?? "#dff0f9";
    const color = darken(backgroundColor, 3.75);
    // horizontal section styles
    const makeHorizontalArrowStyles = (section) => {
        const BORDER_COLOR = "#707070";
        return {
            borderTop: `2px solid ${BORDER_COLOR}`,
            position: "relative",
            "&::before, &::after": {
                border: "solid transparent",
                content: "' '",
                display: "block",
                height: 0,
                position: "absolute",
                pointerEvents: "none",
                width: 0,
                bottom: "100%",
                opacity: activeSection === section ? 1 : 0,
            },
            "&::before": {
                borderColor: "rgba(255, 255, 255, 0)",
                borderBottomColor: BORDER_COLOR,
                left: "50%",
                marginLeft: "-45px",
                borderWidth: "45px",
            },
            "&::after": {
                borderColor: "rgba(255, 255, 255, 0)",
                borderBottomColor: "#fff",
                left: "50%",
                marginLeft: "-42px",
                borderWidth: "42px",
            },
        };
    };
    const ICON_STYLES = {
        color,
        px: { xs: 3, md: 4, lg: 8 },
        my: -4,
        "& > svg": {
            width: "100%",
        },
    };
    const HEADING_STYLES = { color, pt: 1.5, pb: { xs: 0.5, lg: 1 } };
    const COLUMN_STYLES = {
        transition: theme.transitions.create(["filter", "transform"], {
            easing: "cubic-bezier(0.4, 0.0, 0.2, 1)",
        }),
        "&:hover": isTouchDevice
            ? {}
            : {
                cursor: "pointer",
                transform: "scale(1.1)",
            },
    };
    return (React.createElement(Box, { component: "section", id: t("anchor-additional-materials", "zusatzmaterialien"), sx: { backgroundColor: "white" } },
        React.createElement(Container, { maxWidth: "xl", sx: { pt: { xs: 8, lg: 14 }, pb: 3 } },
            React.createElement(Typography, { variant: "h2", sx: { pb: 0.5 } }, t("additional-materials-headline", "Lass dich inspirieren")),
            React.createElement(Typography, { variant: "body1" }, t("additional-materials-copy-1", "So machst du aus deinem Krimidinnner „{{title}}“ etwas ganz Besonderes.", { title }))),
        React.createElement(Container, { maxWidth: "xl", sx: { pb: { xs: 6, lg: 8 }, pt: 3, position: "relative", width: "100%", zIndex: 5 } },
            React.createElement(Grid, { alignItems: "baseline", container: true, spacing: 0, sx: { mt: 0 } },
                React.createElement(Grid, { sm: 3 },
                    React.createElement(Stack, { alignItems: "center", direction: "column", spacing: 0, sx: COLUMN_STYLES, onClick: () => setActiveSection("recipes") },
                        React.createElement(RecipesIcon, { borderColor: colors.secondary, box: { sx: ICON_STYLES } }),
                        React.createElement(Typography, { sx: HEADING_STYLES, variant: "h3" }, t("recipes", "Rezeptideen")),
                        React.createElement(Typography, { variant: "body1" }, t("recipes-subheadline", "Danke, Rülps …")))),
                React.createElement(Grid, { sm: 3 },
                    React.createElement(Stack, { alignItems: "center", direction: "column", spacing: 0, sx: COLUMN_STYLES, onClick: () => setActiveSection("costumes") },
                        React.createElement(CostumeIcon, { borderColor: colors.secondary, box: { sx: ICON_STYLES } }),
                        React.createElement(Typography, { sx: HEADING_STYLES, variant: "h3" }, t("costumes", "Kostüme")),
                        React.createElement(Typography, { variant: "body1" }, t("costumes-subheadline", "Sein, oder nicht sein …")))),
                React.createElement(Grid, { sm: 3 },
                    React.createElement(Stack, { alignItems: "center", direction: "column", spacing: 0, sx: COLUMN_STYLES, onClick: () => setActiveSection("decoration") },
                        React.createElement(DecorationIcon, { borderColor: colors.secondary, box: { sx: ICON_STYLES } }),
                        React.createElement(Typography, { sx: HEADING_STYLES, variant: "h3" }, t("decoration", "Dekoration")),
                        React.createElement(Typography, { variant: "body1" }, t("decoration-subheadline", "… oder lebst du noch?")))),
                React.createElement(Grid, { sm: 3 },
                    React.createElement(Stack, { alignItems: "center", direction: "column", spacing: 0, sx: COLUMN_STYLES, onClick: () => setActiveSection("music") },
                        React.createElement(MusicIcon, { borderColor: colors.secondary, box: { sx: ICON_STYLES } }),
                        React.createElement(Typography, { sx: HEADING_STYLES, variant: "h3" }, t("music", "Musik")),
                        React.createElement(Typography, { variant: "body1" }, t("music-subheadline", "Trrr Tuschhhh…"))))),
            React.createElement(Grid, { container: true, spacing: 0, sx: { mt: { xs: 10, lg: 12 } } },
                React.createElement(Grid, { sm: 3, sx: makeHorizontalArrowStyles("recipes") }),
                React.createElement(Grid, { sm: 3, sx: makeHorizontalArrowStyles("costumes") }),
                React.createElement(Grid, { sm: 3, sx: makeHorizontalArrowStyles("decoration") }),
                React.createElement(Grid, { sm: 3, sx: makeHorizontalArrowStyles("music") }))),
        React.createElement(Container, { maxWidth: "xl", sx: {
                backgroundColor: "white",
                pb: { xs: 6, lg: 14 },
                position: "relative",
                width: "100%",
                zIndex: 10,
            } },
            !!recipes && activeSection === "recipes" && (React.createElement(Stack, { direction: "column", spacing: 4, sx: { maxWidth: "70ch" } },
                React.createElement(Box, null,
                    React.createElement(Typography, { component: "h4", variant: "h5", sx: { pt: 0, pb: 0 } }, t("starter", "Vorspeise")),
                    React.createElement(Typography, { dangerouslySetInnerHTML: { __html: recipes?.starter }, component: "div", variant: "body1", sx: { pt: 0, pb: 0, mt: 0, p: { marginBlockStart: "0 !important" } } })),
                React.createElement(Box, null,
                    React.createElement(Typography, { component: "h4", variant: "h5", sx: { pt: 0, pb: 0 } }, t("main-dish", "Hauptgang")),
                    React.createElement(Typography, { dangerouslySetInnerHTML: { __html: recipes?.main }, component: "div", variant: "body1", sx: { pt: 0, pb: 0, mt: 0, p: { marginBlockStart: "0 !important" } } })),
                React.createElement(Box, null,
                    React.createElement(Typography, { component: "h4", variant: "h5", sx: { pt: 0, pb: 0 } }, t("dessert", "Dessert")),
                    React.createElement(Typography, { dangerouslySetInnerHTML: { __html: recipes?.dessert }, component: "div", variant: "body1", sx: { pt: 0, pb: 0, mt: 0, p: { marginBlockStart: "0 !important" } } })))),
            activeSection === "costumes" && (React.createElement(React.Fragment, null,
                React.createElement(Typography, { variant: "body1", sx: { maxWidth: "70ch" } }, t("costumes-copy-1", "Niemand muss sich verkleiden, aber mit Verkleidung macht es gleich doppelt so viel Spaß. Lasst eurer Fantasie freien Lauf oder greift auf unsere Kostümvorschläge zurück.")),
                React.createElement(CostumeSuggestions, null))),
            activeSection === "decoration" && (React.createElement(React.Fragment, null,
                decoration && (React.createElement(Typography, { dangerouslySetInnerHTML: { __html: decoration }, component: "div", variant: "body1", sx: { maxWidth: "70ch" } })),
                !decoration && (React.createElement(Typography, { variant: "body1", sx: { maxWidth: "70ch" } }, t("decoration-copy-is-missing", "Wir haben für diesen Fall leider noch keine Dekorationsideen hinterlegt."))))),
            activeSection === "music" && (React.createElement(React.Fragment, null,
                music && (React.createElement(Typography, { dangerouslySetInnerHTML: { __html: music }, component: "div", variant: "body1", sx: { maxWidth: "70ch" } })),
                !music && (React.createElement(Typography, { variant: "body1", sx: { maxWidth: "70ch" } }, t("music-copy-is-missing", "Wir haben für diesen Fall leider noch keine Musik-Playlist hinterlegt."))))))));
};
//# sourceMappingURL=horizontal-layout.js.map